import React from "react";
import { Sparkles, X, ArrowRight, TrendingUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import CalendlyButton from "@/components/site/CalendlyButton";

const ranges = [
  "Under $5,000",
  "$5,000 – $25,000",
  "$25,000 – $100,000",
  "$100,000 – $500,000",
  "$500,000 – $1M",
  "Above $1M",
];
const times = [
  "Anytime",
  "Morning (9 AM – 12 PM)",
  "Afternoon (12 PM – 5 PM)",
  "Evening (5 PM – 9 PM)",
];

export default function LeadFormSticky({ source = "home" }) {
  const [open, setOpen] = React.useState(false);
  const [show, setShow] = React.useState(false);
  const viewedRef = React.useRef(false);

  React.useEffect(() => {
    const t = setTimeout(() => setShow(true), 800);
    return () => clearTimeout(t);
  }, []);

  const openDialog = () => {
    if (!viewedRef.current) {
      trackEvent("lead_form_view", { source });
      viewedRef.current = true;
    }
    setOpen(true);
  };

  if (!show) return null;

  return (
    <>
      <div className="fixed bottom-6 left-6 z-[55] hidden sm:block" data-testid="lead-sticky">
        <button
          onClick={openDialog}
          data-testid="lead-sticky-trigger"
          className="group relative inline-flex items-center gap-3 rounded-full border border-[#D4AF37]/40 bg-[#0B1221]/90 backdrop-blur-xl pl-2 pr-5 py-2 shadow-[0_10px_30px_rgba(212,175,55,0.18)] hover:shadow-[0_15px_45px_rgba(212,175,55,0.35)] hover:border-[#D4AF37] transition-all"
        >
          <span className="w-9 h-9 rounded-full bg-[#D4AF37] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#0A1128]" />
          </span>
          <span className="text-left">
            <span className="block text-[10px] uppercase tracking-widest text-[#D4AF37]">Free</span>
            <span className="block text-sm font-medium text-white">Portfolio Review</span>
          </span>
          <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition" />
        </button>
      </div>

      {/* Mobile floating pill */}
      <button
        onClick={openDialog}
        data-testid="lead-sticky-mobile"
        className="sm:hidden fixed bottom-24 left-4 right-4 z-[55] inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/50 bg-[#0B1221]/95 backdrop-blur-xl px-4 py-3 text-sm font-medium text-[#D4AF37] shadow-[0_15px_45px_rgba(212,175,55,0.25)]"
      >
        <Sparkles className="w-4 h-4" /> Get Your Free Portfolio Review
      </button>

      <LeadDialog open={open} onOpenChange={setOpen} source={source} />
    </>
  );
}

export function LeadDialog({ open, onOpenChange, source = "home" }) {
  const [form, setForm] = React.useState({
    name: "", email: "", phone: "",
    investment_range: ranges[1], preferred_time: times[0],
  });
  const [loading, setLoading] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source });
      trackEvent("lead_form_submit", { source, investment_range: form.investment_range });
      toast.success("Thank you! An advisor will reach out within 24 hours.");
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({ name: "", email: "", phone: "", investment_range: ranges[1], preferred_time: times[0] });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="lead-dialog"
        className="bg-[#0B1221] border-white/10 text-white sm:max-w-lg p-0 overflow-hidden"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.18),transparent_55%),radial-gradient(circle_at_bottom_left,rgba(14,165,233,0.18),transparent_55%)]" />
          <div className="relative p-7 sm:p-8">
            {!done ? (
              <>
                <DialogHeader className="space-y-3 text-left">
                  <div className="inline-flex items-center gap-2 self-start rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-1 w-fit">
                    <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Complimentary</span>
                  </div>
                  <DialogTitle className="font-[Outfit] text-3xl font-light tracking-tight text-white">
                    Free Portfolio Review
                  </DialogTitle>
                  <DialogDescription className="text-slate-400 text-sm">
                    Tell us a little about your goals — a dedicated advisor will respond within 24 hours.
                  </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="mt-6 space-y-4">
                  <FormField label="Full name">
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                      data-testid="lead-name"
                      placeholder="Your full name"
                      className="bg-[#111827] border-white/10 text-white placeholder:text-slate-500 focus:border-sky-500"
                    />
                  </FormField>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormField label="Email">
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        data-testid="lead-email"
                        placeholder="you@example.com"
                        className="bg-[#111827] border-white/10 text-white placeholder:text-slate-500 focus:border-sky-500"
                      />
                    </FormField>
                    <FormField label="Mobile number">
                      <Input
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        required
                        data-testid="lead-phone"
                        placeholder="+971 50 123 4567"
                        className="bg-[#111827] border-white/10 text-white placeholder:text-slate-500 focus:border-sky-500"
                      />
                    </FormField>
                  </div>
                  <FormField label="Investment amount range">
                    <Select value={form.investment_range} onValueChange={(v) => setForm({ ...form, investment_range: v })}>
                      <SelectTrigger data-testid="lead-range" className="bg-[#111827] border-white/10 text-white focus:border-sky-500">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0B1221] border-white/10 text-white">
                        {ranges.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>
                  <FormField label="Preferred contact time">
                    <Select value={form.preferred_time} onValueChange={(v) => setForm({ ...form, preferred_time: v })}>
                      <SelectTrigger data-testid="lead-time" className="bg-[#111827] border-white/10 text-white focus:border-sky-500">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0B1221] border-white/10 text-white">
                        {times.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </FormField>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="lead-submit"
                    className="btn-gold w-full mt-2"
                  >
                    {loading ? "Sending…" : "Get my free review"} <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-[10px] text-slate-500 text-center">
                    By submitting, you agree to be contacted by an MFB advisor. We never share your details.
                  </p>
                </form>
              </>
            ) : (
              <div className="text-center py-6" data-testid="lead-success">
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="mt-5 font-[Outfit] text-2xl text-white font-light">Request received.</h3>
                <p className="mt-2 text-slate-400 text-sm">
                  An MFB advisor will reach out within 24 hours. Want to lock in a time now?
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
                  <CalendlyButton label="Book a slot now" source="lead_success" testId="lead-success-calendly" />
                  <button onClick={reset} className="btn-ghost" data-testid="lead-success-close">
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
